import {
    type FC,
    type PropsWithChildren,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useNavigate } from "react-router";
import {
    LayerProvider,
    RouterContext,
    type RouterContextType,
    type UIContextType,
    UIProvider,
} from "@onshopfront/components";
import { Application } from "#/_Bootstrap/Application.ts";
import { useShopfrontSelector } from "#/_Bootstrap/hooks.ts";
import {
    getOption,
    type Options,
    type OptionSubscriberCallback,
    subscribeToOption,
    unsubscribeFromOption,
} from "#/Utilities/Platform/Options/Options.ts";
import { isDarkMode } from "../DarkMode/DarkModeHooks.ts";

const FLOATING_ROOT = "floating-root-container";

const DAY_OF_WEEK: Array<Options["start_of_week"]> = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
];

export const UIContainer: FC<PropsWithChildren> = ({ children }) => {
    const navigate = useNavigate();

    const userId = useShopfrontSelector(state => state.user.id);

    // Allow the decimalSeparator and decimalPlaces to be inferred from the currency options
    const [ context, setContext ] = useState<
        Omit<UIContextType, "decimalSeparator" | "decimalPlaces">
    >(() => ({
        viewportHeight: "calc(100vh - 50px)",
        currency      : getOption("currency_code") || "AUD",
        locale        : getOption("number_locale") || "en-AU",
        rootId        : FLOATING_ROOT,
        scrollBody    : undefined,
        darkMode      : isDarkMode(),
        startOfWeek   : Math.max(DAY_OF_WEEK.indexOf(getOption("start_of_week")), 0),
        floatingZIndex: 1,
        theme         : {
            links: "", // Removing the default styling for links
        },
    }));

    useEffect(() => {
        const darkModeManager = Application.get("DarkModeManager");

        const onDarkModeChange = () => {
            setContext(prevState => ({
                ...prevState,
                darkMode: darkModeManager.isDarkMode(),
            }));
        };

        const onCurrencyChange: OptionSubscriberCallback<"currency_code"> = (_, newValue) => {
            setContext(prevState => ({
                ...prevState,
                currency: newValue || "AUD",
            }));
        };

        const onLocaleChange: OptionSubscriberCallback<"number_locale"> = (_, newValue) => {
            setContext(prevState => ({
                ...prevState,
                locale: newValue || "en-AU",
            }));
        };

        const onStartOfWeekChange: OptionSubscriberCallback<"start_of_week"> = (_, newValue) => {
            setContext(prevState => ({
                ...prevState,
                startOfWeek: Math.max(DAY_OF_WEEK.indexOf(newValue), 0),
            }));
        };

        darkModeManager.addListener(onDarkModeChange);

        subscribeToOption("currency_code", onCurrencyChange);
        subscribeToOption("number_locale", onLocaleChange);
        subscribeToOption("start_of_week", onStartOfWeekChange);

        return () => {
            darkModeManager.removeListener(onDarkModeChange);

            unsubscribeFromOption("currency_code", onCurrencyChange);
            unsubscribeFromOption("number_locale", onLocaleChange);
            unsubscribeFromOption("start_of_week", onStartOfWeekChange);
        };
    }, []);

    const routerContext = useMemo<RouterContextType>(() => ({
        type: "normal",
        navigate,
    }), [ navigate ]);

    const zIndexOverride = useMemo(() => {
        if(userId !== false) {
            return undefined;
        }

        return {
            toast: 5,
        };
    }, [ userId ]);

    return (
        <RouterContext value={routerContext}>
            <UIProvider value={context}>
                <LayerProvider
                    zIndex={1}
                    zIndexOverride={zIndexOverride}
                >
                    {children}
                </LayerProvider>
                <div
                    id={FLOATING_ROOT}
                    className="pointer-events-none absolute inset-0 overflow-hidden"
                />
            </UIProvider>
        </RouterContext>
    );
};
